import { Check, Server, Shuffle } from 'lucide-react';
import { TASK_AI_BUILDER_NAME, type TaskAiMachine } from '@shared/types/tasks';
import { CommandItem } from '@/components/ui/command';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import { LoadError } from '@/components/LoadError';
import { aiMachineMatches } from './use-ai-machines';

/**
 * SF-T-174: the AI builder on a chosen fleet machine, as a picker offers it (Assign, Waiting on). The
 * machine stays on the task until someone changes it, so its runs go there (the factory waits while it's
 * busy); "any machine" lets the factory choose again.
 */
export interface AiMachineChoice {
  /** The machines it can be sent to (online, with a confirmed checkout); null while they load. */
  machines: TaskAiMachine[] | null;
  /** The machine chosen for the task, or null. */
  selected: TaskAiMachine | null;
  /** Ask the AI builder on this machine; null lets the factory choose. */
  onPick: (machineId: string | null) => void;
  /** The picker opened: load the machines now (they're only asked for when needed). */
  load?: () => void;
  /** They couldn't be loaded, and how to ask again. */
  failed?: boolean;
  retry?: () => void;
}

/**
 * The choices under the AI builder in a picker's Command list: one per machine ("on jackalope", ticked when
 * it's the task's), and "on any machine" once one is chosen. `search` filters them in a list that filters
 * by hand (shouldFilter off); a list that filters itself matches their values. The chosen machine is listed
 * even while it's offline, so it shows as chosen.
 */
export function AiMachineItems({ choice, search = '', onPicked }: { choice: AiMachineChoice; search?: string; onPicked?: () => void }) {
  const pick = (machineId: string | null) => {
    choice.onPick(machineId);
    onPicked?.();
  };
  const { selected } = choice;
  const listed = choice.machines ?? [];
  const machines = (selected && !listed.some((m) => m.id === selected.id) ? [selected, ...listed] : listed).filter((m) => aiMachineMatches(m, search));
  return (
    <>
      {choice.machines === null && choice.failed && !search.trim() ? (
        <div className="py-1 pl-8 pr-2">
          <LoadError compact title="Couldn't load the machines" onRetry={choice.retry} />
        </div>
      ) : choice.machines === null && !search.trim() ? (
        <div className="flex items-center gap-2 py-1.5 pl-8 pr-2 text-xs text-muted-foreground">
          <Spinner size="xs" />
          Loading machines…
        </div>
      ) : null}
      {machines.map((m) => (
        <CommandItem key={m.id} value={`${TASK_AI_BUILDER_NAME} on ${m.name} ${m.id}`} onSelect={() => pick(m.id)} className="pl-8">
          <Server className="size-4 text-muted-foreground" />
          <span className="flex-1 truncate">on {m.name}</span>
          {selected?.id === m.id ? <Check className="size-4" /> : null}
        </CommandItem>
      ))}
      {selected && (!search.trim() || 'any machine'.includes(search.trim().toLowerCase())) ? (
        <CommandItem value={`${TASK_AI_BUILDER_NAME} on any machine`} onSelect={() => pick(null)} className="pl-8">
          <Shuffle className="size-4 text-muted-foreground" />
          <span className="flex-1 truncate">on any machine</span>
        </CommandItem>
      ) : null}
    </>
  );
}

/**
 * The task's chosen machine as a chip in its properties ("on jackalope"), with an X that lets the factory
 * choose again (asking nothing).
 */
export function AiMachineChip({ machine, onClear }: { machine: TaskAiMachine; onClear?: () => void }) {
  return (
    <Badge
      variant="outline"
      data-hint={`The ${TASK_AI_BUILDER_NAME} runs this task on ${machine.name}, waiting while it's busy`}
      onRemove={onClear}
      removeLabel={`Let the ${TASK_AI_BUILDER_NAME} choose its machine instead of ${machine.name}`}
    >
      <Server aria-hidden />
      <span className="max-w-[10rem] truncate">on {machine.name}</span>
    </Badge>
  );
}
